import React from 'react';
import {StyleSheet, View, Image} from 'react-native';
import Styles from '../../Styles';
import Localized from 'src/constants/AppStrings';
import AVText from 'src/components/elements/AVText';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import {ensureHttps} from 'src/services/StringUtils';
import AVFormattedCurrency from '../AVFormattedCurrency';
import {DNA} from 'src/types/dnaTypes';
import {MenuProductType} from 'src/types/Menu';
import AccountStore from 'src/stores/AccountStore';
import {
  getCalorieIndicator,
  getOverrideCalorieIndicator,
} from './healthGradeHelper';
import {getDescriber} from 'src/components/elements/orderAhead/descriptor/DescriptorType';
import TransactionStore from 'src/stores/TransactionStore';
import Settings from 'src/Settings';

const {getMenuListCompnentsFontFamily} = getDescriber();

type MenuItemListTemplate2Props = {
  imageUrl?: string;
  name: string;
  price: number;
  item?: MenuProductType;
  dnaConfiguration?: DNA;
  onPress: () => void;
  isProductCat?: boolean;
  index?: number;
  overrideDna?: string;
};

const MenuItemListTemplate2 = (props: MenuItemListTemplate2Props) => {
  /** initialize */
  const displayHealthGrade =
    props?.dnaConfiguration?.showColorCodes &&
    !props?.dnaConfiguration?.isDisabled;
  const calorieUnitName = props?.dnaConfiguration?.showCaloriesAsKilocalories
    ? Localized.Labels.kcal
    : Localized.Labels.cal_not_all_caps;
  const caloriesLabel = props.item?.calories;
  const isNewRewardPointsProvided = props?.item.rewardPoints > 0;
  const newRewardPolicyPoints = props?.item.rewardPoints;
  const rewardPointsLabel = 'rewardPoints';

  const parsedCalorieValueNew =
    typeof caloriesLabel === 'number'
      ? caloriesLabel
      : parseInt('' + caloriesLabel);

  const showCalories =
    props.dnaConfiguration?.showCalories && props.item?.calories !== null;

  const showNutrionGrade =
    displayHealthGrade &&
    !isNaN(parsedCalorieValueNew) &&
    parsedCalorieValueNew >= 0;

  /** health grade component */
  const {IconComponent, label, hint} = getCalorieIndicator(
    parsedCalorieValueNew,
    props?.dnaConfiguration,
  );
  const overrideDna = props.item?.overrideDna;
  const overrideResult =
    overrideDna && overrideDna !== 'Nope'
      ? getOverrideCalorieIndicator(overrideDna)
      : null;

  const OverrideIcon = overrideResult?.overrideComponent ?? null;
  const rewardProductData = TransactionStore.getrewardItem();
  return (
    <AVTouchableOpacity
      onPress={props.onPress}
      accessible
      accessibilityLabel={[
        props.name,
        showCalories || showNutrionGrade
          ? `${caloriesLabel} ${calorieUnitName}`
          : '',
        isNewRewardPointsProvided
          ? Localized.Labels.formatString(
              Localized.Labels.points_number,
              newRewardPolicyPoints,
            )
          : '',
        AccountStore.getCurrency() + props.price.toFixed(2),
      ].join('. ')}
      accessibilityHint="Double tap to navigate to the product details screen"
    >
      <View style={styles.container}>
        <View style={styles.labelView}>
          <AVText
            accessible
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
            style={styles.productNameText}
            accessibilityLabel={`${props.name}, Text`}
            accessibilityRole="text"
            aria-label={`${props.name}, Text`}
            numberOfLines={1}
            testID="qa_ItemName_txt"
            nativeID="qa_ItemName_txt"
          >
            {props.name}
          </AVText>
          {(showCalories || showNutrionGrade) && (
            <View style={styles.calView}>
              {showCalories && (
                <AVText
                  accessible
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
                  style={styles.caloriesLabel}
                  accessibilityLabel={`${caloriesLabel} ${calorieUnitName}, Text`}
                  accessibilityRole="text"
                  aria-label={`${caloriesLabel} ${calorieUnitName}, Text`}
                  testID="qa_ItemCal_txt"
                  nativeID="qa_ItemCal_txt"
                >
                  {`${caloriesLabel} ${calorieUnitName}`}
                </AVText>
              )}

              {showNutrionGrade && (
                <View
                  accessible={true}
                  accessibilityHint={hint}
                  accessibilityLabel={`${Localized.Labels.health_grade}: ${label}, `}
                  aria-label={`${Localized.Labels.health_grade}: ${label}, `}
                  accessibilityRole="image"
                  role="img"
                  style={styles.healthGrade}
                >
                  {overrideDna && overrideDna !== 'Nope' ? (
                    <OverrideIcon
                      width={Styles.Spacing.m3 + 2}
                      height={Styles.Spacing.m3 + 2}
                    />
                  ) : (
                    <IconComponent
                      width={Styles.Spacing.m3 + 2}
                      height={Styles.Spacing.m3 + 2}
                    />
                  )}
                </View>
              )}
            </View>
          )}

          {isNewRewardPointsProvided && (
            <View style={styles.pointsContainer}>
              <AVText
                accessible
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm1}
                style={styles.newRewardPolicyPointsLabel}
                accessibilityLabel={`${rewardPointsLabel} Text`}
                accessibilityRole="text"
                aria-label={`${rewardPointsLabel} Text`}
              >
                {Localized.Labels.formatString(
                  Localized.Labels.points_number,
                  newRewardPolicyPoints,
                )}
              </AVText>

              {(props?.item.id === rewardProductData?.Id ||
                props?.item?.points > 1) && (
                <View style={styles.blackDotSymbol} />
              )}
              {(props?.item.id === rewardProductData?.Id ||
                props?.item.points > 1) && (
                <AVText
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm0}
                  style={styles.transPoints}
                >
                  {props?.item.id === rewardProductData?.Id
                    ? Localized.Labels.reward_applied
                    : (Localized.Labels.formatString(
                        Localized.Labels.reward_points_multiply,
                        props?.item.points,
                      ) as string)}
                </AVText>
              )}
            </View>
          )}
          <View>
            <AVFormattedCurrency
              value={props.price}
              style={[styles.priceText]}
              accessible={true}
              accessibilityLabel={`$${props.price.toFixed(2)}, text, `}
              aria-label={`$${props.price.toFixed(2)}, `}
              currency={AccountStore.getCurrency()}
              maxFractionDigits={2}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
              testID="qa_ItemPrice_txt"
              nativeID="qa_ItemPrice_txt"
            />
          </View>
        </View>
        <View style={styles.imageViewMargin}>
          {props.imageUrl ? (
            <Image
              accessible
              aria-label={`${props.name} Image`}
              accessibilityLabel={`${props.name} Image`}
              role="img"
              accessibilityRole="image"
              source={{uri: ensureHttps(props.imageUrl)}}
              style={styles.menuImage}
            />
          ) : (
            <View style={styles.menuImage} />
          )}
        </View>
      </View>
    </AVTouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Styles.Colors.PayNew.white01,
    borderRadius: Styles.Spacing.m2,
    borderWidth: 1,
    borderColor: Styles.Colors.PayNew.neutralHuesBase11,
    marginHorizontal: Styles.Spacing.m3,
    marginBottom: Styles.Spacing.m2 + 2,
    justifyContent: 'space-between',
    flexDirection: 'row',
    ...Styles.Style.lightShadow,
  },
  labelView: {
    flex: 1,
    justifyContent: 'space-around',
    marginLeft: Styles.Spacing.m2 + Styles.Spacing.m1,
    marginTop: Styles.Spacing.m2,
    marginBottom: Styles.Spacing.m2,
    marginRight: Styles.Spacing.m2 + 2,
  },
  productNameText: {
    color: Styles.Colors.PayNew.black01,
    fontWeight: '700',
    fontFamily: getMenuListCompnentsFontFamily(),
    fontSize: Styles.Fonts.f1,
  },
  healthGrade: {
    marginTop: Styles.Spacing.m2 - 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: -1,
  },
  caloriesLabel: {
    fontFamily: getMenuListCompnentsFontFamily(),
    fontSize: Styles.Fonts.f1,
    fontWeight: '400',
    color: Styles.Colors.PayNew.darkHuesBase03,
  },
  calView: {
    flexDirection: 'row',
  },
  priceText: {
    fontFamily: getMenuListCompnentsFontFamily(),
    fontSize: Styles.Fonts.f7,
    fontWeight: '700',
    color: Styles.Colors.PayNew.black01,
  },
  imageView: {
    width: 117,
    height: 118,
    borderBottomRightRadius: Styles.Spacing.m2,
    borderTopRightRadius: Styles.Spacing.m2,
  },
  menuImage: {
    width: 117,
    height: 94,
    borderBottomRightRadius: Styles.Spacing.m2,
    borderTopRightRadius: Styles.Spacing.m2,
    flex: 1,
  },
  imageViewMargin: {marginLeft: Styles.Spacing.m1},
  pointsView: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: Styles.Spacing.m1,
    marginBottom: Styles.Spacing.m1,
  },
  blackDotSymbol: {
    width: Styles.Spacing.m2,
    height: Styles.Spacing.m2,
    borderRadius: Styles.Spacing.m1,
    backgroundColor: Styles.black,
    marginHorizontal: Styles.Spacing.m2,
  },
  transPoints: {
    fontSize: Styles.Fonts.f8,
    fontWeight: '400',
    color: Settings.isRefiveAnd365PayOnly()
      ? Styles.Colors.PayNew.primary01
      : Styles.Colors.Canteen.green,
    fontFamily: getMenuListCompnentsFontFamily(),
  },
  imageContainer: {
    flex: 1,
    backgroundColor: Styles.Colors.PayNew.white01,
    borderRadius: Styles.Spacing.m2,
    borderWidth: 1,
    borderColor: Styles.Colors.PayNew.neutralHuesBase11,
    marginHorizontal: Styles.Spacing.m3,
    marginBottom: Styles.Spacing.m2 + 2,
    justifyContent: 'space-between',
    flexDirection: 'row',
    height: 120,
    ...Styles.Style.lightShadow,
  },
  pointsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  newRewardPolicyPointsLabel: {
    fontFamily: getMenuListCompnentsFontFamily(),
    fontSize: Styles.Fonts.f1,
    fontWeight: '400',
    color: Styles.Colors.PayNew.darkHuesBase03,
  },
});

export default MenuItemListTemplate2;
